import { ActivityCategory, ActivityType, EventType } from 'types/models';
import { Box, Flex, Input, Select, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import {
    DashboardPageContent,
    FiltersWrapper,
    FormWrapper,
    PageSubtitle,
    PageTitle,
} from 'custom-style-components';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
    downloadMinistrantsXls,
    downloadPresenceListPdf,
    downloadRegistryPdf,
    downloadRegistryXls,
} from 'utils/reportUtils';
import {
    fetchManyActivitiesByEvent,
    fetchOneActivity,
} from 'services/fetch/activities';
import { useLocation, useParams } from 'react-router-dom';

import ActivityTable from 'components/ActivityTable';
import ConfirmDeleteComponent from 'components/ConfirmDeleteComponent';
import CustomButton from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import { GenericFrontError } from 'errors/GenericFrontError';
import GoBackButton from 'components/GoBackButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { ResponseError } from 'errors/ResponseError';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyCategories } from 'services/fetch/activityCategories';
import { fetchManyRegistriesByActivity } from 'services/fetch/registries';
import { fetchOneEvent } from 'services/fetch/events';
import renderEventName from 'utils/renderEventName';

interface ParamTypes {
    eventId: string;
}

const ListActivities: React.FC = () => {
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const { eventId } = useParams<ParamTypes>();
    const { pathname } = useLocation();

    const [event, setEvent] = useState<EventType>();
    const [categoryList, setCategoryList] = useState<ActivityCategory[]>();
    const [activityList, setActivityList] = useState<ActivityType[]>([]);
    const [activityShow, setActivityShow] = useState<ActivityType[]>([]);
    const [activityName, setActivityName] = useState('');
    const [debouncedActivityName, setDebouncedActivityName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<number>();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(-1);

    const source = useRef(createCancelTokenSource());

    const timeoutRef = useRef<number>();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedActivityName(activityName);
        }, 400);
        return () => clearTimeout(handler);
    }, [activityName]);

    const onChangeCategory = (ev: ChangeEvent<HTMLSelectElement>) => {
        if (ev.target.value != undefined) {
            setLoading(true);

            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = window.setTimeout(() => {
                setSelectedCategory(
                    ev.target.value === '' ? undefined : parseInt(ev.target.value),
                );
            }, 200);
        }
    };

    const downloadXls = (activityId: number, extension: 'xls' | 'xlsx') => {
        toast({ title: 'Baixando presença .' + extension, duration: 2000 });
        const src = createCancelTokenSource();
        let activity: ActivityType;
        fetchOneActivity(src.token, activityId.toString())
            .then((result) => {
                activity = result;
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                });
            })
            .then(({ registries }) => downloadRegistryXls(activity, registries, extension));
    };

    const downloadPdf = (activityId: number) => {
        toast({ title: 'Baixando relatório .pdf', duration: 2000 });
        const src = createCancelTokenSource();
        let activity: ActivityType;
        fetchOneActivity(src.token, activityId.toString())
            .then((result) => {
                activity = result;
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                });
            })
            .then(({ registries }) => downloadRegistryPdf(activity, registries));
    };

    const downloadXlsMinisters = (activityId: number, extension: 'xls' | 'xlsx') => {
        toast({ title: 'Baixando ministrantes .' + extension, duration: 2000 });
        const src = createCancelTokenSource();
        fetchOneActivity(src.token, activityId.toString()).then((result) => {
            downloadMinistrantsXls(result, extension);
        });
    };

    const downloadPdfPresence = (activityId: number) => {
        toast({ title: 'Baixando lista de presença .pdf', duration: 2000 });
        const src = createCancelTokenSource();
        let activity: ActivityType;
        fetchOneActivity(src.token, activityId.toString())
            .then((result) => {
                activity = result;
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                });
            })
            .then(({ registries }) => downloadPresenceListPdf(activity, registries));
    };

    const loadActivities = useCallback(() => {
        source.current = createCancelTokenSource();
        setLoading(true);
        fetchOneEvent(source.current.token, eventId)
            .then((ev) => {
                setEvent(ev);
                return fetchManyActivitiesByEvent(source.current.token, eventId, {
                    limit: Number.MAX_SAFE_INTEGER,
                    category: selectedCategory,
                });
            })
            .then(({ activities }) => {
                setActivityList(activities);
                setActivityShow(activities);
            })
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            })
            .finally(() => setLoading(false));
    }, [eventId, selectedCategory, toast]);

    useEffect(() => {
        fetchManyCategories(source.current.token, { limit: Number.MAX_SAFE_INTEGER })
            .then((result) => setCategoryList(result.categories))
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            });
    }, []);

    useEffect(() => {
        loadActivities();
        return () => source.current.cancel();
    }, [selectedCategory]);

    useEffect(() => {
        const lower = debouncedActivityName.toLowerCase();
        setActivityShow(
            activityList.filter(
                (a) =>
                    a.title.toLowerCase().includes(lower) ||
                    a.activityCategory.description.toLowerCase().includes(lower),
            ),
        );
    }, [debouncedActivityName, activityList]);

    const handleDeleteClick = useCallback((id: number) => {
        setIdToDelete(id);
        setConfirmDeleteModalOpen(true);
    }, []);

    const deleteActivity = async (id: number) => {
        try {
            await api.delete(`/activity/${eventId}/${id}`);
            toast({
                title: 'Atividade excluída',
                description: `Exclusão da atividade ${id}`,
                status: 'success',
            });
            loadActivities();
        } catch (err) {
            let message = 'Erro';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError && err.status === 404)
                    message = 'Atividade não encontrada';
                if (err instanceof ResponseError && err.status === 400)
                    message = 'O evento desta atividade está acontecendo, não é possível excluí-la';
            }
            toast({ title: 'Erro na exclusão', description: message, status: 'error' });
        } finally {
            setConfirmDeleteModalOpen(false);
        }
    };



    return (
        <DashboardPageContent>
            <FormWrapper>
                <ConfirmDeleteComponent
                    modalOpen={confirmDeleteModalOpen}
                    setModalOpen={setConfirmDeleteModalOpen}
                    handleDelete={() => deleteActivity(idToDelete)}
                />

                <PageTitle>Atividades</PageTitle>
                <PageSubtitle>{event && renderEventName(event)}</PageSubtitle>
                <GoBackButton />

                <CustomButton
                    link={pathname + '/criar_atividade'}
                    style={{ marginBottom: '0.8rem', marginLeft: '0.8rem' }}
                >
                    Cadastrar atividade
                </CustomButton>

                {/* Filtros e Pesquisa */}
                <FiltersWrapper>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" mt={4} mb={6}>
                        <Box>
                            <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                Categorias
                            </Text>
                            {categoryList && categoryList.length ? (
                                <Select
                                    bg="white"
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    placeholder="Todas as categorias"
                                    onChange={onChangeCategory}
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                >
                                    {categoryList.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Text fontSize="lg" color="gray.500">
                                    Não foi possível carregar a lista de categorias
                                </Text>
                            )}
                        </Box>

                        <Box>
                            <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                Pesquisar atividades
                            </Text>
                            <Input
                                size="lg"
                                fontSize="xl"
                                h="50px"
                                bg="white"
                                type="text"
                                value={activityName}
                                onChange={(e) => setActivityName(e.target.value)}
                                placeholder="Pesquise por nome ou categoria"
                                focusBorderColor="blue.400"
                                shadow="sm"
                                borderRadius="md"
                                isDisabled={activityList.length === 0}
                            />
                        </Box>
                    </SimpleGrid>
                </FiltersWrapper>

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : loading ? (
                    <Flex justify="center" p={10}>
                        <LoadingSpinner />
                    </Flex>
                ) : (
                    <ActivityTable
                        activities={activityShow}
                        pathname={pathname}
                        showEditAction
                        showPresencasAction
                        onDeleteClick={handleDeleteClick}

                        onDownloadPresencasXls={downloadXls}
                        onDownloadMinistrantesXls={downloadXlsMinisters}
                        onDownloadListaPresencaPdf={downloadPdfPresence}
                        onDownloadRelatorioPdf={downloadPdf}
                    />
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default ListActivities;
